import { resolveTeamPalette } from "../../../../lib/teamPalette";
import TeamLogo from "../../../../components/TeamLogo";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";

interface ScoreBoardProps {
  t: any;
  match: any;
  liveScore: { home: number; away: number };
  queuedCount: number;
  pendingAckCount: number;
  transitionError: string | null;
  toastMessage?: string;
  /** PMA-COCK-106 (3rd revision): true when rendered inside the landscape-
   * tablet rail (a ~200px column, narrower than the hero grid's
   * `min-w-[260px]` assumption). Picked in JS, not CSS, for the `TeamLogo`
   * size specifically: its `size` prop maps to a fixed inline
   * `style.width/height` (`TeamLogo.tsx`), which always beats a responsive
   * Tailwind class regardless of specificity — the same lesson
   * `TacticalField.tsx`'s viewport-cap fix already recorded. Reintroduced
   * after two scope changes: v1 (4-component rail, withdrawn) had this;
   * v2 (RefereeActionBar-only, shipped as 555a0b5) removed it since
   * ScoreBoard never entered the rail there; v3 (this) restores it —
   * ScoreBoard + MatchTimerDisplay + RefereeActionBar in the rail,
   * TeamSelector deliberately excluded (its formation pickers are
   * structurally two-sided, built to sit near their own half of the pitch —
   * the one component the lead's own correction kept out on that basis). */
  isRailLayout?: boolean;
}

export default function ScoreBoard({
  t,
  match,
  liveScore,
  queuedCount,
  pendingAckCount,
  transitionError,
  toastMessage,
  isRailLayout = false,
}: ScoreBoardProps) {
  // EPIC-111 UIJ-R3-2: below 1024px wide (no rail) the hero collapses to a
  // single row (crest + name + score | VS | score + name + crest) so the
  // pitch fits the fold. JS, not CSS, because TeamLogo sizes inline.
  const isCompactRow = useMediaQuery("(max-width: 1023px)") && !isRailLayout;
  const homePalette = resolveTeamPalette(match.home_team);
  const awayPalette = resolveTeamPalette(match.away_team);
  // ui-judge round-1 defect 3: the team name/score spans below used
  // `homePalette.primary`/`awayPalette.primary` as text color on this
  // component's dark slate-900/slate-800 gradient card — the away rose-700
  // swatch measured 2.84:1, home amber-700 3.56:1, both below the 4.5:1
  // (large-text 3.0:1) WCAG AA floor. `.primary` is tuned for solid
  // fills/borders, not text on a dark surface; `.textOnDark` is the
  // already-established AA-safe token for exactly this (same pattern
  // `PublicMatchAnalytics.tsx`'s own score header and `PublicTeamSheet.tsx`
  // already use).

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-col flex-1">
        {/* PMA-COCK-106: the standalone "{home} vs {away}" title line that used
            to render here was removed — it repeated, verbatim, the two team
            names the hero block immediately below already shows next to each
            crest. No information was lost, ~40px was (display text only, no
            testid, no i18n key removed from the codebase's *used* set — the
            `matchTitle` key itself is left in the locale files, just no
            longer called from this component). Score stays the visually
            dominant element (frontend-design: preserve hierarchy — the clock
            and score are what an operator glances at, they should not shrink
            to the same weight as secondary chrome just because the layout
            got denser). Hero block compaction below: py-4->py-2.5,
            5xl/6xl->3xl/4xl score digits — still clearly the largest text
            on the page, just not oversized.

            `isRailLayout` (JS, not CSS) switches the 3-column hero grid to a
            2-row stack — the fixed `min-w-[260px]`/3-column grid assumes
            ~260px+ of width, which the ~200px rail column doesn't have. JS,
            not a CSS breakpoint, because ScoreBoard now genuinely renders in
            two different DOM locations (CockpitTopSection.tsx normally,
            LoggerView.tsx's rail when `isRailLayout`) rather than one
            location that reflows — there's no single viewport condition
            that's "this is the rail" independent of which parent mounted
            it. */}
        <div className={isRailLayout ? "w-full" : "w-full flex justify-center"}>
          <div
            className={
              isRailLayout
                ? "relative overflow-hidden rounded-xl border border-slate-700/60 shadow-lg bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-3 py-2 flex flex-col gap-2"
                : isCompactRow
                  ? "relative overflow-hidden rounded-xl border border-slate-700/60 shadow-lg bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-3 py-1.5 grid grid-cols-[1fr_auto_1fr] items-center gap-2 w-full"
                  : "relative overflow-hidden rounded-2xl border border-slate-700/60 shadow-lg bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-5 sm:px-7 py-2.5 grid grid-cols-[minmax(120px,0.9fr)_minmax(190px,1.5fr)_minmax(120px,0.9fr)] sm:grid-cols-[minmax(150px,0.85fr)_minmax(260px,1.6fr)_minmax(150px,0.85fr)] items-center gap-3 sm:gap-5 min-w-[260px]"
            }
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.06),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.04),transparent_30%)]" />
            <div
              className={
                isRailLayout
                  ? "flex items-center justify-between z-10 min-w-0 w-full"
                  : isCompactRow
                    ? "flex flex-row items-center justify-start gap-2 z-10 min-w-0"
                    : "flex flex-col items-center z-10 min-w-0"
              }
            >
              <div className="flex items-center gap-2 max-w-full min-w-0">
                <TeamLogo
                  team={match.home_team}
                  size={
                    isRailLayout || isCompactRow
                      ? isRailLayout
                        ? "sm"
                        : "md"
                      : "xl"
                  }
                  bare
                />
                <span
                  className={
                    isRailLayout
                      ? "text-xs uppercase tracking-wide break-words leading-tight text-center"
                      : "text-sm sm:text-base uppercase tracking-wide break-words leading-tight max-w-full text-center"
                  }
                  style={{ color: homePalette.textOnDark }}
                >
                  {match.home_team.name}
                </span>
              </div>
              <span
                className={
                  isRailLayout
                    ? "text-2xl font-black drop-shadow"
                    : "text-3xl sm:text-4xl font-black drop-shadow"
                }
                style={{ color: homePalette.textOnDark }}
                data-testid="home-score"
              >
                {liveScore.home || match.home_team.score || 0}
              </span>
            </div>
            {!isRailLayout && (
              <div className="z-10 flex flex-col items-center px-2 sm:px-4">
                <span
                  className={
                    isCompactRow
                      ? "sr-only"
                      : "text-sm font-semibold text-slate-400"
                  }
                >
                  {t("statusLabel", "Status")}
                </span>
                <span className="text-xl sm:text-2xl font-black text-slate-200">
                  {t("vs", "VS")}
                </span>
              </div>
            )}
            <div
              className={
                isRailLayout
                  ? "flex items-center justify-between z-10 min-w-0 w-full"
                  : isCompactRow
                    ? "flex flex-row-reverse items-center justify-start gap-2 z-10 min-w-0"
                    : "flex flex-col items-center z-10 min-w-0"
              }
            >
              <div className="flex items-center gap-2 max-w-full min-w-0">
                <TeamLogo
                  team={match.away_team}
                  size={
                    isRailLayout || isCompactRow
                      ? isRailLayout
                        ? "sm"
                        : "md"
                      : "xl"
                  }
                  bare
                />
                <span
                  className={
                    isRailLayout
                      ? "text-xs uppercase tracking-wide break-words leading-tight text-center"
                      : "text-sm sm:text-base uppercase tracking-wide break-words leading-tight text-center max-w-full"
                  }
                  style={{ color: awayPalette.textOnDark }}
                >
                  {match.away_team.name}
                </span>
              </div>
              <span
                className={
                  isRailLayout
                    ? "text-2xl font-black drop-shadow"
                    : "text-3xl sm:text-4xl font-black drop-shadow"
                }
                style={{ color: awayPalette.textOnDark }}
                data-testid="away-score"
              >
                {liveScore.away || match.away_team.score || 0}
              </span>
            </div>
          </div>
        </div>
        {/* PMA-COCK-115: the per-goal "GOL" log board (clock + scorer chips
            below the score, e.g. "1:46.000 · #5 Christian Martinez") was
            removed per client request — "para eso está la analítica." The
            score itself (above) is untouched. `goalEvents`/`formatGoalLabel`
            (still computed in `useLiveScore`, CockpitContext.tsx) are no
            longer threaded to this component since this was their only
            consumer — see useLiveScore.ts if a future caller needs them. */}
      </div>
      <div className="flex items-center gap-2 text-xs">
        {queuedCount > 0 && (
          <span
            className="flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold border border-amber-200"
            title="Events queued for send"
          >
            🔄 {t("queuedLabel", "Queued: {{count}}", { count: queuedCount })}
          </span>
        )}
        {pendingAckCount > 0 && (
          <span
            className="flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-800 font-semibold border border-blue-200"
            title="Awaiting server acknowledgements"
          >
            ⏳{" "}
            {t("pendingAckLabel", "Pending acks: {{count}}", {
              count: pendingAckCount,
            })}
          </span>
        )}
        {(transitionError || toastMessage) && (
          <span
            className="flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-800 font-semibold border border-red-200"
            title="Last error"
          >
            ⚠️ {transitionError || toastMessage}
          </span>
        )}
      </div>
    </div>
  );
}
